"use client";

import { useSession, useSupabaseClient } from "@/components/SupabaseProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { ProfileModel } from "../types/models";
import { Database } from "../types/supabase";

export const PROFILE_QUERY_KEY = "profile";

// Module-level so callers outside the hook (e.g. VideoInput's dialog
// preload) can prefetch the profile into the same react-query cache entry.
export const fetchProfileByUserId = async (
  supabaseClient: SupabaseClient<Database>,
  userId: string
): Promise<ProfileModel> => {
  return supabaseClient
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single()
    .then((r) => {
      if (r.error) throw r.error;
      if (!r.data) throw new Error("Author's videos not found");
      return deserializeProfile(r.data);
    });
};
export const useProfile = (refetchInterval?: number) => {
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const session = useSession();

  const updateProfile = async (update: Partial<ProfileModel>) => {
    const data = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/users/me`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session?.access_token}`,
      },
      body: JSON.stringify(serializeProfile(update)),
    }).then((res) => res.json());
    return data;
  };

  const updateMutation = useMutation({
    mutationFn: updateProfile,
    onMutate: async (update) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [PROFILE_QUERY_KEY],
      });

      // Snapshot the previous value
      const previousUpdate = queryClient.getQueryData([PROFILE_QUERY_KEY]);

      // Optimistically update to the new value
      queryClient.setQueryData([PROFILE_QUERY_KEY], (old: any) => {
        return { ...old, ...update };
      });

      // Return a context object with the snapshotted value
      return { previousUpdate };
    },

    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (_err, _newTodo, context) => {
      queryClient.setQueryData([PROFILE_QUERY_KEY], context?.previousUpdate);
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: [PROFILE_QUERY_KEY] });
    },
  });

  return {
    ...useQuery({
      queryKey: [PROFILE_QUERY_KEY],
      refetchInterval: refetchInterval ?? false,
      enabled: !!session?.user.id,
      queryFn: async () => {
        if (!session?.user.id) return;
        return fetchProfileByUserId(supabaseClient, session.user.id);
      },
    }),
    update: updateMutation,
  };
};

const deserializeProfile = (
  data: Database["public"]["Tables"]["profiles"]["Row"]
): ProfileModel => {
  return {
    id: data.id,
    email: data.email,
    avatarUrl: data.avatar_url,
    name: data.name,
    defaultPresetId: data.default_preset_id,
    customerId: data.customer_id,
    stripeSubscriptionId: data.stripe_subscription_id,
    subscriptionStatus:
      data.subscription_status as ProfileModel["subscriptionStatus"],
    rollingVideoCount: data.rolling_video_count,
    rollingClipCount: data.rolling_clip_count,
    rollingExportCount: data.rolling_export_count,
    // @ts-ignore
    exportCountLimit: data.export_count_limit,
    // Columns exist; generated types may lag behind migrations (hence casts).
    rollingUploadMinuteCount:
      (data as { rolling_upload_minute_count?: number })
        .rolling_upload_minute_count ?? 0,
    uploadMinuteCountLimit:
      (data as { upload_minute_count_limit?: number })
        .upload_minute_count_limit ?? 0,
    // @ts-ignore — column exists; generated types may lag behind migrations
    creditCount: (data as { credit_count?: number }).credit_count ?? 0,
    plan: data.plan as ProfileModel["plan"],
    videoCountLimit: data.video_count_limit,
    // @ts-ignore
    clipCountLimit: data.clip_count_limit,
    uploadDurationAllowanceSeconds: data.upload_duration_allowance_seconds,
    onboardingSurveyAnswered: data.onboarding_survey_answered as string[],
    // @ts-ignore
    onboardingSurveyAnswers: data.onboarding_survey_answers as Record<
      string,
      string | string[]
    >,
    createdAt: new Date(data.created_at),
    emailProjectReady: data.email_project_ready,
    emailExportReady: data.email_export_ready,
    emailProductUpdates: data.email_product_updates,
    // Column exists; generated types may lag behind migrations (hence cast).
    emailPostPublished:
      (data as { email_post_published?: boolean }).email_post_published ?? true,
    // @ts-ignore
    organizationId: data.organization_id,
    // @ts-ignore
    isGuest: data.is_guest,
    // @ts-ignore
    isAdmin: data.is_admin,
    // @ts-ignore
    referralCode: data.referral_code,
  };
};

const serializeProfile = (
  data: Partial<ProfileModel>
): Partial<Database["public"]["Tables"]["profiles"]["Row"]> => {
  return {
    email: data.email,
    avatar_url: data.avatarUrl,
    name: data.name,
    // Generated Supabase types mark default_preset_id as non-nullable,
    // but the column accepts null and we use it for "no user default".
    default_preset_id: data.defaultPresetId as string,
    customer_id: data.customerId,
    subscription_status: data.subscriptionStatus as string,
    rolling_video_count: data.rollingVideoCount,
    rolling_clip_count: data.rollingClipCount,
    rolling_export_count: data.rollingExportCount,
    // @ts-ignore
    export_count_limit: data.exportCountLimit,
    // @ts-ignore
    credit_count: data.creditCount,
    plan: data.plan,
    video_count_limit: data.videoCountLimit,
    clip_count_limit: data.clipCountLimit,
    upload_duration_allowance_seconds: data.uploadDurationAllowanceSeconds,
    onboarding_survey_answered: data.onboardingSurveyAnswered,
    onboarding_survey_answers: data.onboardingSurveyAnswers,
    created_at: data.createdAt?.toISOString(),
    email_project_ready: data.emailProjectReady,
    email_export_ready: data.emailExportReady,
    email_product_updates: data.emailProductUpdates,
    // @ts-ignore
    email_post_published: data.emailPostPublished,
    // @ts-ignore
    is_guest: data.isGuest,
    // @ts-ignore
    referral_code: data.referralCode,
  };
};
